import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Lock, Unlock, ShieldCheck, AlertTriangle, PlusCircle, Loader2, X, Clock, FileSignature,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import FormattedMarkdown from "@/components/FormattedMarkdown";

/**
 * Panel that governs lifecycle of a consultation:
 *   • Encerrar consulta (signature by password)
 *   • Adicionar adendo (immutable appendix per CFM Res. 1.638/2002)
 *   • Reabrir consulta (doctor/admin only, with mandatory reason)
 *
 * The parent passes the full `consultation` doc plus a `refresh` callback so
 * the panel can reload state after every action. Nothing about the surrounding
 * form fields is coupled here — the parent uses `consultation.locked_at` to
 * make its own fields readonly.
 */
const GRACE_HOURS = 24;

function withinGrace(lockedAtIso) {
    if (!lockedAtIso) return false;
    const locked = new Date(lockedAtIso);
    if (Number.isNaN(locked.getTime())) return false;
    return Date.now() - locked.getTime() < GRACE_HOURS * 3600 * 1000;
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
}

export default function ConsultationLockPanel({ consultation, canEdit, isDoctor, onChange }) {
    const [addendums, setAddendums] = useState(consultation.addendums || []);
    const [showLock, setShowLock] = useState(false);
    const [showReopen, setShowReopen] = useState(false);
    const [showAddendum, setShowAddendum] = useState(false);

    useEffect(() => { setAddendums(consultation.addendums || []); }, [consultation]);

    const isLocked = !!consultation.locked_at;
    const inGrace = isLocked && withinGrace(consultation.locked_at);

    if (!isLocked) {
        return (
            <>
                {canEdit && (
                    <div
                        className="card-earthy mb-4 border-l-4 border-[var(--brand-accent)] bg-alt/60 flex items-center justify-between gap-4 flex-wrap"
                        data-testid="lock-panel-active"
                    >
                        <div className="flex items-start gap-3">
                            <ShieldCheck size={22} className="text-brand shrink-0 mt-0.5" />
                            <div className="text-sm">
                                <p className="font-medium text-brand">Consulta em andamento</p>
                                <p className="text-muted-clinical text-xs mt-0.5">
                                    Ao final do atendimento, encerre a consulta para congelar o prontuário
                                    (CFM Res. 1.638/2002). Correções ainda serão possíveis por 24h; depois disso, apenas adendos.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowLock(true)}
                            data-testid="open-lock-modal-btn"
                            className="btn-primary shrink-0"
                        >
                            <Lock size={16} /> Encerrar consulta
                        </button>
                    </div>
                )}
                {showLock && (
                    <LockModal
                        consultationId={consultation.id}
                        onClose={() => setShowLock(false)}
                        onLocked={() => { setShowLock(false); onChange && onChange(); }}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <div
                className="card-earthy mb-4 border-l-4 border-brand bg-brand/5"
                data-testid="lock-panel-locked"
            >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center shrink-0">
                            <Lock size={18} />
                        </div>
                        <div className="text-sm">
                            <p className="font-medium text-brand flex items-center gap-2">
                                Consulta encerrada
                                {inGrace && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--status-warning-bg)] text-[var(--status-warning)] font-semibold uppercase tracking-widest">
                                        <Clock size={10} className="inline mr-1" /> Janela de correção 24h
                                    </span>
                                )}
                            </p>
                            <p className="text-muted-clinical text-xs mt-0.5">
                                Encerrada em <strong>{fmtDateTime(consultation.locked_at)}</strong>
                                {consultation.locked_by_name && <> por <strong>{consultation.locked_by_name}</strong></>}.
                                {inGrace
                                    ? " Você ainda pode ajustar pequenos detalhes até completar 24 horas do encerramento."
                                    : " Apenas adendos formais podem ser adicionados."}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <button
                            onClick={() => setShowAddendum(true)}
                            data-testid="open-addendum-modal-btn"
                            className="btn-primary"
                        >
                            <PlusCircle size={14} /> Adicionar adendo
                        </button>
                        {isDoctor && (
                            <button
                                onClick={() => setShowReopen(true)}
                                data-testid="open-reopen-modal-btn"
                                className="btn-secondary"
                                title="Reabrir a consulta (requer justificativa registrada)"
                            >
                                <Unlock size={14} /> Reabrir
                            </button>
                        )}
                    </div>
                </div>

                {addendums.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-brand/10">
                        <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-secondary mb-3">
                            Adendos ({addendums.length})
                        </div>
                        <ul className="space-y-3">
                            {addendums.map((a) => (
                                <li key={a.id} className="border-l-2 border-[var(--brand-accent)] pl-3" data-testid={`addendum-${a.id}`}>
                                    <div className="text-xs text-muted-clinical mb-1">
                                        {fmtDateTime(a.created_at)} · {a.author_name || "—"}
                                        {a.author_role && <> ({a.author_role})</>}
                                        {a.section && <> · seção: <em>{a.section}</em></>}
                                    </div>
                                    <div className="text-sm text-[var(--text-primary)] whitespace-pre-wrap">{a.content}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {consultation.reopen_log && consultation.reopen_log.length > 0 && (
                    <details className="mt-4 pt-3 border-t border-brand/10 text-xs">
                        <summary className="cursor-pointer text-muted-clinical hover:text-brand">
                            Histórico de reaberturas ({consultation.reopen_log.length})
                        </summary>
                        <ul className="mt-2 space-y-2">
                            {consultation.reopen_log.map((r, i) => (
                                <li key={i} className="pl-2 border-l border-[var(--brand-accent)]">
                                    <div>{fmtDateTime(r.reopened_at)} · {r.reopened_by_name || "—"}</div>
                                    <div className="text-muted-clinical italic">"{r.reason}"</div>
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </div>

            {showAddendum && (
                <AddendumModal
                    consultationId={consultation.id}
                    onClose={() => setShowAddendum(false)}
                    onCreated={(a) => {
                        setAddendums((prev) => [...prev, a]);
                        setShowAddendum(false);
                        onChange && onChange();
                    }}
                />
            )}

            {showReopen && (
                <ReopenModal
                    consultationId={consultation.id}
                    onClose={() => setShowReopen(false)}
                    onReopened={() => { setShowReopen(false); onChange && onChange(); }}
                />
            )}
        </>
    );
}

// ---------- Modais ----------
function ModalShell({ title, icon: Icon, testId, onClose, children, footer }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testId}>
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
                <div className="p-5 border-b border-[var(--border-organic)] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Icon size={20} className="text-brand" />
                        <h2 className="font-display text-xl font-medium text-brand">{title}</h2>
                    </div>
                    <button onClick={onClose} className="text-muted-clinical hover:text-brand"><X size={20} /></button>
                </div>
                <div className="p-5 space-y-3">{children}</div>
                <div className="p-4 border-t border-[var(--border-organic)] flex justify-end gap-2">
                    {footer}
                </div>
            </div>
        </div>
    );
}

function LockModal({ consultationId, onClose, onLocked }) {
    const [password, setPassword] = useState("");
    const [ack1, setAck1] = useState(false);
    const [ack2, setAck2] = useState(false);
    const [busy, setBusy] = useState(false);
    const canSubmit = password.length > 0 && ack1 && ack2 && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            await api.post(`/consultations/${consultationId}/lock`, { password });
            toast.success("Consulta encerrada e assinada eletronicamente.");
            onLocked();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <ModalShell title="Encerrar e assinar consulta" icon={FileSignature} testId="lock-modal" onClose={onClose}
            footer={
                <>
                    <button onClick={onClose} className="btn-secondary" disabled={busy}>Cancelar</button>
                    <button onClick={submit} disabled={!canSubmit} data-testid="confirm-lock-btn"
                        className="px-4 py-2 rounded-lg bg-brand text-white font-medium hover:bg-brand-dark disabled:opacity-50 flex items-center gap-2">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Encerrar consulta
                    </button>
                </>
            }
        >
            <div className="text-sm text-[var(--text-secondary)] rounded-lg bg-alt p-3 flex items-start gap-2">
                <AlertTriangle size={16} className="text-[var(--brand-accent)] shrink-0 mt-0.5" />
                <span>
                    Você está assinando eletronicamente este prontuário. Após 24h,
                    <strong> nenhum campo poderá ser editado</strong> — apenas adendos formais.
                    Esta assinatura serve como equivalente ao seu registro profissional (CRM/RQE).
                </span>
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={ack1} onChange={(e) => setAck1(e.target.checked)}
                    data-testid="lock-ack-review" className="mt-1" />
                <span>Revisei os dados clínicos, medicações e conduta desta consulta.</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={ack2} onChange={(e) => setAck2(e.target.checked)}
                    data-testid="lock-ack-legal" className="mt-1" />
                <span>Estou ciente da imutabilidade após 24h e concordo em assinar o prontuário.</span>
            </label>
            <div>
                <label className="label-clinical">Sua senha atual (assinatura eletrônica)</label>
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="lock-password-input"
                    className="input-clinical"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && canSubmit && submit()}
                />
            </div>
        </ModalShell>
    );
}

function AddendumModal({ consultationId, onClose, onCreated }) {
    const [content, setContent] = useState("");
    const [section, setSection] = useState("");
    const [busy, setBusy] = useState(false);
    const canSubmit = content.trim().length >= 5 && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/consultations/${consultationId}/addendum`,
                { content: content.trim(), section: section.trim() || null });
            toast.success("Adendo registrado com data, hora e autoria.");
            onCreated(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <ModalShell title="Adicionar adendo ao prontuário" icon={PlusCircle} testId="addendum-modal" onClose={onClose}
            footer={
                <>
                    <button onClick={onClose} className="btn-secondary" disabled={busy}>Cancelar</button>
                    <button onClick={submit} disabled={!canSubmit} data-testid="confirm-addendum-btn" className="btn-primary">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <PlusCircle size={16} />} Salvar adendo
                    </button>
                </>
            }
        >
            <p className="text-xs text-muted-clinical">
                O adendo é uma nota anexa e imutável — o conteúdo original da consulta permanece intacto.
                Assinado automaticamente com seu nome e horário atual.
            </p>
            <div>
                <label className="label-clinical">Seção referente (opcional)</label>
                <input
                    type="text"
                    value={section}
                    onChange={(e) => setSection(e.target.value)}
                    placeholder="Ex.: Reconciliação medicamentosa"
                    className="input-clinical"
                    data-testid="addendum-section-input"
                />
            </div>
            <div>
                <label className="label-clinical">Conteúdo do adendo</label>
                <textarea
                    rows={6}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Descreva a informação complementar ou correção..."
                    className="input-clinical text-sm"
                    data-testid="addendum-content-input"
                    autoFocus
                />
                <div className="text-[11px] text-muted-clinical mt-1">
                    Mínimo 5 caracteres. Preview do markdown:
                </div>
                {content.trim().length > 0 && (
                    <div className="mt-2 p-2 rounded bg-alt text-sm">
                        <FormattedMarkdown text={content} />
                    </div>
                )}
            </div>
        </ModalShell>
    );
}

function ReopenModal({ consultationId, onClose, onReopened }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const canSubmit = reason.trim().length >= 10 && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            await api.post(`/consultations/${consultationId}/reopen`, { reason: reason.trim() });
            toast.success("Consulta reaberta. A justificativa foi registrada.");
            onReopened();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <ModalShell title="Reabrir consulta encerrada" icon={Unlock} testId="reopen-modal" onClose={onClose}
            footer={
                <>
                    <button onClick={onClose} className="btn-secondary" disabled={busy}>Cancelar</button>
                    <button onClick={submit} disabled={!canSubmit} data-testid="confirm-reopen-btn"
                        className="px-4 py-2 rounded-lg bg-[var(--brand-accent)] text-white font-medium disabled:opacity-50 flex items-center gap-2 hover:opacity-90">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Unlock size={16} />} Reabrir
                    </button>
                </>
            }
        >
            <p className="text-sm rounded-lg bg-[var(--status-warning-bg)] text-[var(--status-warning)] p-3">
                Toda reabertura é <strong>registrada permanentemente</strong> no prontuário com sua identificação e a
                justificativa abaixo. Use este recurso apenas em situações realmente excepcionais.
            </p>
            <div>
                <label className="label-clinical">Justificativa (mínimo 10 caracteres)</label>
                <textarea
                    rows={4}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Ex.: Corrigir dose de losartana incorretamente prescrita — paciente retornou hoje."
                    className="input-clinical text-sm"
                    data-testid="reopen-reason-input"
                    autoFocus
                />
                <div className="text-[11px] text-muted-clinical mt-1">
                    {reason.trim().length}/10 caracteres mínimos
                </div>
            </div>
        </ModalShell>
    );
}
